import type { ConsortiumCategory } from '@/types'

export interface ConsortiumSimulation {
    credit: number
    term: number
    feeRate: number
    adminFeeValue: number
    reserveFundValue: number
    total: number
    installment: number
    financingInstallment: number
    financingTotal: number
    difference: number
}

export const calculatePriceInstallment = (principal: number, monthlyRate: number, months: number): number => {
    if (monthlyRate <= 0) return principal / months

    return principal * monthlyRate / (1 - Math.pow(1 + monthlyRate, -months))
}

export const simulateConsortium = (category: ConsortiumCategory, credit: number, term: number): ConsortiumSimulation => {
    const adminFeeValue = credit * category.adminFee
    const reserveFundValue = credit * category.reserveFund
    const total = credit + adminFeeValue + reserveFundValue
    const financingInstallment = calculatePriceInstallment(credit, category.financing.monthlyRate, term)
    const financingTotal = financingInstallment * term

    return {
        credit,
        term,
        feeRate: category.adminFee + category.reserveFund,
        adminFeeValue,
        reserveFundValue,
        total,
        installment: total / term,
        financingInstallment,
        financingTotal,
        difference: financingTotal - total
    }
}

export const getLongestTerm = (category: ConsortiumCategory): number => Math.max(...category.terms)

export const getStartingInstallment = (category: ConsortiumCategory): number => {
    return simulateConsortium(category, category.credit.min, getLongestTerm(category)).installment
}

export const clampCredit = (category: ConsortiumCategory, value: number): number => {
    return Math.min(category.credit.max, Math.max(category.credit.min, value))
}

export const isValidTerm = (category: ConsortiumCategory, term: number): boolean => category.terms.includes(term)
